/**
 * Countdown timer for the Dynamic Date-Range Banner.
 *
 * Drives `.countdown-timer-banner[data-end-date]` — NOT the theme's own
 * <countdown-timer> custom element in assets/countdown-timer.js. Those are two
 * separate implementations; see the header of snippets/countdown-timer-banner.liquid.
 *
 * Shows days/hours/minutes only and updates once per minute, scheduled to the
 * exact minute boundary of the remaining time (no seconds unit: WCAG 2.2.2
 * decision, PR #142 review). Writes only the digits that actually changed. The
 * ported original rewrote every value on every requestAnimationFrame. PR #74
 * review, blocker #3. A timer removed from the DOM (Theme Editor re-render)
 * stops itself on its next tick.
 *
 * At zero (or for an end date already in the past) the whole timer is hidden and
 * a bubbling `countdown:end` event is dispatched from it.
 *
 * An unparseable data-end-date leaves the markup's static "00" placeholders in
 * place rather than painting a live-looking 00:00:00:00 under a still-live
 * banner. The Liquid guard in the snippet prevents the known trigger (blank end
 * date), but the schema's end_date is a free-text field, so a merchant typing
 * "12/31/2027" can still reach this path.
 */
(function () {
  var MINUTE_MS = 60000;
  var UNITS = 3; // days, hours, minutes. No seconds (WCAG 2.2.2, PR #142 review).

  function render(boxes, last, values) {
    for (var i = 0; i < UNITS; i++) {
      var next = String(values[i]).padStart(2, '0');
      if (last[i] === next) continue;
      last[i] = next;
      boxes[i].textContent = next;
    }
  }

  function start(timer) {
    var endDate = new Date(timer.getAttribute('data-end-date')).getTime();

    // Invalid Date -> NaN. Bail out and leave the server-rendered placeholders.
    if (isNaN(endDate)) return;

    var boxes = timer.querySelectorAll('.countdown-timer-banner__number');
    if (boxes.length !== UNITS) return;

    var last = [null, null, null];
    var timeoutId = null;

    function stop() {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }

    function update() {
      timeoutId = null;

      // Theme Editor re-renders replace the section; a detached timer must not
      // keep ticking forever.
      if (!timer.isConnected) {
        stop();
        return;
      }

      var timeLeft = endDate - Date.now();

      if (timeLeft <= 0) {
        render(boxes, last, [0, 0, 0]);
        // A finished countdown is not shown frozen at 00:00:00 under a
        // possibly cached page: hide the whole timer (CSS honours [hidden]).
        timer.hidden = true;
        stop();
        // Hosts that own the surrounding offer (the announcement bar's expiry
        // guard) remove it on this event instead of keeping their own clock.
        try {
          timer.dispatchEvent(new CustomEvent('countdown:end', { bubbles: true }));
        } catch (e) {
          /* CustomEvent unsupported: the host's own expiry check still runs */
        }
        return;
      }

      // Round UP to whole minutes so a live deadline never reads 00:00:00;
      // the display reaches zero exactly when the deadline does.
      var totalMinutes = Math.ceil(timeLeft / MINUTE_MS);
      render(boxes, last, [
        Math.floor(totalMinutes / 1440),
        Math.floor((totalMinutes % 1440) / 60),
        totalMinutes % 60,
      ]);

      // Next change happens when timeLeft crosses the next whole minute.
      var untilNextMinute = timeLeft % MINUTE_MS || MINUTE_MS;
      timeoutId = setTimeout(update, untilNextMinute);
    }

    update();
  }

  function init(root) {
    var scope = root || document;
    var timers = scope.querySelectorAll('.countdown-timer-banner[data-end-date]');

    for (var i = 0; i < timers.length; i++) {
      // Prevent re-initialization if this timer was already initialized.
      if (timers[i].dataset.countdownInitialized) continue;
      timers[i].dataset.countdownInitialized = 'true';
      start(timers[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
